/**
 * Map playdate builder draft fixtures to CirclePlaydate schedule inputs.
 *
 * Daypart: Morning / Afternoon / Evening → representative local instants.
 * Exact: user-selected HH:mm in the playdate IANA timezone.
 * Presentation mode is persisted via note envelope (no schema migration).
 */

import type {
  PlaydateBuilderDraft,
  PlaydateTimeWindow,
} from '@/lib/dev-preview-play/playdateBuilderFixtures'
import { getPlaydateParkFixture } from '@/lib/dev-preview-play/playdateBuilderFixtures'
import { encodeCirclePlaydateNote } from '@/lib/play/circlePlaydatePlanMeta'
import { CIRCLE_PLAYDATE_MIN_DURATION_MS } from '@/lib/play/circlePlaydateConstants'
import {
  resolvePlaydateParkOption,
  type PlaydateParkOption,
} from '@/lib/play/playParkHandoff'

const WINDOW_HOURS: Record<PlaydateTimeWindow, { start: number; end: number }> = {
  morning: { start: 9, end: 10 },
  afternoon: { start: 14, end: 15 },
  evening: { start: 17, end: 18 },
}

function localHourInZone(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(date)
  const raw = parts.find((p) => p.type === 'hour')?.value ?? '12'
  return Number(raw === '24' ? '0' : raw)
}

/** Local calendar midnight for `base` in `timeZone`, as a UTC Date. */
export function startOfLocalDay(base: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(base)
  const year = Number(parts.find((p) => p.type === 'year')?.value)
  const month = Number(parts.find((p) => p.type === 'month')?.value)
  const day = Number(parts.find((p) => p.type === 'day')?.value)
  const guess = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  const localHour = localHourInZone(guess, timeZone)
  const utcHour = guess.getUTCHours()
  const offsetHours = utcHour - localHour
  let midnight = new Date(Date.UTC(year, month - 1, day, offsetHours, 0, 0))
  const refineHour = localHourInZone(midnight, timeZone)
  if (refineHour !== 0) {
    midnight = new Date(midnight.getTime() - refineHour * 60 * 60 * 1000)
  }
  return midnight
}

export function addLocalDays(dateId: string, now: Date, timeZone: string): Date {
  const base = startOfLocalDay(now, timeZone)
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(now)

  const addDays = (n: number) => new Date(base.getTime() + n * 24 * 60 * 60 * 1000)

  switch (dateId) {
    case 'today':
      return base
    case 'tomorrow':
      return addDays(1)
    case 'saturday': {
      const map: Record<string, number> = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
      }
      const todayIdx = map[weekday] ?? 0
      const delta = (6 - todayIdx + 7) % 7 || 7
      return addDays(delta === 0 ? 7 : delta)
    }
    case 'sunday': {
      const map: Record<string, number> = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
      }
      const todayIdx = map[weekday] ?? 0
      const delta = (0 - todayIdx + 7) % 7 || 7
      return addDays(delta === 0 ? 7 : delta)
    }
    case 'next_week':
      return addDays(7)
    case 'in_two_weeks':
      return addDays(14)
    default:
      return addDays(1)
  }
}

export function atLocalHour(dayStartApprox: Date, hour: number, timeZone: string): Date {
  return atLocalHourMinute(dayStartApprox, hour, 0, timeZone)
}

export function atLocalHourMinute(
  dayStartApprox: Date,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const candidate = new Date(
    dayStartApprox.getTime() + hour * 60 * 60 * 1000 + minute * 60 * 1000
  )
  const localHour = localHourInZone(candidate, timeZone)
  const drift = hour - localHour
  let adjusted = new Date(candidate.getTime() + drift * 60 * 60 * 1000)

  // Refine minutes after hour drift correction.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(adjusted)
  const gotHour = Number(
    (parts.find((p) => p.type === 'hour')?.value ?? '0') === '24'
      ? '0'
      : parts.find((p) => p.type === 'hour')?.value ?? '0'
  )
  const gotMinute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
  const hourDrift = hour - gotHour
  const minuteDrift = minute - gotMinute
  adjusted = new Date(
    adjusted.getTime() + hourDrift * 60 * 60 * 1000 + minuteDrift * 60 * 1000
  )
  return adjusted
}

export type CirclePlaydateBuilderSchedulePayload = {
  scheduledStartAt: string
  scheduledEndAt: string
  timezone: string
  /** Prisma Park.id when known; omitted for fixture / label-only parks. */
  parkId?: string | null
  customLocationLabel: string
  note: string | null
}

export function buildCirclePlaydateScheduleFromDraft(
  draft: PlaydateBuilderDraft,
  options?: {
    timeZone?: string
    now?: Date
    /** Extra parks (e.g. Parks handoff) beyond fixture list. */
    parkOptions?: readonly PlaydateParkOption[]
    /** Force label-only propose after PARK_NOT_FOUND. */
    stripCanonicalParkId?: boolean
  }
): CirclePlaydateBuilderSchedulePayload | null {
  if (!draft.parkId || !draft.dateId) return null
  const timeMode = draft.timeMode ?? 'daypart'
  if (timeMode === 'daypart' && !draft.timeWindow) return null
  if (timeMode === 'exact' && !draft.exactTime) return null

  const fromOptions = options?.parkOptions
    ? resolvePlaydateParkOption(draft.parkId, options.parkOptions)
    : null
  const fixture = getPlaydateParkFixture(draft.parkId)
  const parkName = fromOptions?.name ?? fixture?.name
  if (!parkName) return null
  const canonicalParkId = options?.stripCanonicalParkId
    ? null
    : fromOptions?.canonicalParkId ?? null

  const timeZone =
    options?.timeZone ??
    (typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Los_Angeles')
  const now = options?.now ?? new Date()
  const day = addLocalDays(draft.dateId, now, timeZone)

  let start: Date
  let end: Date

  if (timeMode === 'exact' && draft.exactTime) {
    const [hRaw, mRaw] = draft.exactTime.split(':')
    const hour = Number(hRaw)
    const minute = Number(mRaw)
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
    start = atLocalHourMinute(day, hour, minute, timeZone)
    end = new Date(start.getTime() + CIRCLE_PLAYDATE_MIN_DURATION_MS)
  } else {
    const window = WINDOW_HOURS[draft.timeWindow!]
    start = atLocalHour(day, window.start, timeZone)
    end = atLocalHour(day, window.end, timeZone)
  }

  const minStart = new Date(now.getTime() + 35 * 60 * 1000)
  if (start.getTime() < minStart.getTime()) {
    const bumpDay = addLocalDays('tomorrow', now, timeZone)
    if (timeMode === 'exact' && draft.exactTime) {
      const [hRaw, mRaw] = draft.exactTime.split(':')
      start = atLocalHourMinute(bumpDay, Number(hRaw), Number(mRaw), timeZone)
      end = new Date(start.getTime() + CIRCLE_PLAYDATE_MIN_DURATION_MS)
    } else {
      const window = WINDOW_HOURS[draft.timeWindow!]
      start = atLocalHour(bumpDay, window.start, timeZone)
      end = atLocalHour(bumpDay, window.end, timeZone)
    }
  }

  return {
    scheduledStartAt: start.toISOString(),
    scheduledEndAt: end.toISOString(),
    timezone: timeZone,
    ...(canonicalParkId ? { parkId: canonicalParkId } : {}),
    customLocationLabel: parkName,
    note: encodeCirclePlaydateNote({
      presentationMode: timeMode === 'exact' ? 'exact' : 'daypart',
      userNote: draft.note.trim() || null,
    }),
  }
}
